import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Profile from '../comp/Profile';
import { useSearchParams, Link } from 'react-router-dom';
import ThemeSwitcher from '../comp/ThemeSwitcher';

const Icons = {
  Doodle: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
  Brain: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
  Lightning: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
  Users: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
  Guest: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  Github: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
};

const DoodleUpInfo = [
  {
    iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
    title: "What is it?",
    icon: Icons.Brain,
    description: "A free real-time collaborative whiteboard. No installations. Just ideas.",
  },
  {
    iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
    title: "Why use it?",
    icon: Icons.Lightning,
    description: `100% Free. Real-time drawing. No sign-up required via link.`,
  },
  {
    iconBg: "bg-pink-100 dark:bg-pink-900/30",
    title: "Who is it for?",
    icon: Icons.Users,
    description: `Teams, Students, Designers, and Friends who love to draw!`,
  },
];

const FloatingShapes = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      
      <motion.svg animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} className="absolute top-20 left-[5%] w-32 h-32 text-indigo-200/60 dark:text-indigo-900/30" viewBox="0 0 200 200" fill="none">
        <path d="M10 100C40 10 90 190 190 100" stroke="currentColor" strokeWidth="12" strokeLinecap="round" style={{strokeDasharray: "20 10"}}/>
      </motion.svg>

      <motion.div 
        initial={{ x: -100, y: 100, opacity: 0 }}
        animate={{ x: '100vw', y: -100, opacity: [0, 1, 1, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'linear', delay: 1 }}
        className="absolute top-1/4 left-0 text-slate-300 dark:text-gray-700"
      >
        <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor" className="transform rotate-12">
          <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
        </svg>
      </motion.div>

      <motion.svg className="absolute top-32 right-[10%] w-28 h-28 text-yellow-300 dark:text-yellow-600/30" viewBox="0 0 100 100">
         <motion.circle cx="50" cy="50" r="20" stroke="currentColor" strokeWidth="4" fill="none" 
            initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ pathLength: { delay: 0.5, type: "spring", duration: 2.5, bounce: 0 }, opacity: { delay: 0.5, duration: 0.01 } }} 
         />
         {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
           <motion.line key={i} x1="50" y1="20" x2="50" y2="5" stroke="currentColor" strokeWidth="4" strokeLinecap="round"
             transform={`rotate(${deg} 50 50)`}
             initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ pathLength: { delay: 1 + (i * 0.1), type: "spring", duration: 1.5, bounce: 0 }, opacity: { delay: 1 + (i * 0.1), duration: 0.01 } }}
           />
         ))}
      </motion.svg>

      <motion.svg animate={{ y: [0, -40, 0], x: [0, 20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-40 left-[15%] w-40 h-40 text-pink-200/50 dark:text-pink-900/20" viewBox="0 0 200 200">
         <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="8" fill="none" style={{strokeDasharray: "30 20"}} />
      </motion.svg>
      
      <motion.div className="hidden md:block absolute bottom-1/3 left-[25%] w-32 h-32 text-indigo-300/80 dark:text-indigo-700/50 -rotate-12">
         <svg viewBox="0 0 100 100" fill="none" className="w-full h-full">
            <motion.path 
              d="M10,10 C40,50 40,10 90,80" 
              stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" 
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 2, duration: 1 }}
            />
            <motion.path 
              d="M70,75 L90,80 L85,60" 
              stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" 
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 2.5, duration: 0.5 }}
            />
         </svg>
         <motion.span 
           initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
           className="font-marker absolute -top-3 -left-8 text-md -rotate-12 text-indigo-400 dark:text-indigo-500"
         >
           Join here!
         </motion.span>
      </motion.div>

      <motion.div animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }} className="absolute top-[40%] right-[5%] text-emerald-100/60 dark:text-emerald-900/20">
        <svg width="120" height="120" viewBox="0 0 100 100" fill="none">
          <path d="M50 50 m 0 0 a 5 5 0 0 1 5 -5 a 10 10 0 0 1 10 10 a 15 15 0 0 1 -15 15 a 20 20 0 0 1 -20 -20 a 25 25 0 0 1 25 -25" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeDasharray="1 10"/>
        </svg>
      </motion.div>

       <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }} className="absolute bottom-20 right-[10%] text-slate-300/70 dark:text-gray-700/50 font-marker text-6xl">
         + + +
      </motion.div>
      
    </div>
  );
};


export default function Home({ data, setData }) {
  const [userName, setUserName] = useState('');
  const [enterName, setEnterName] = useState(false);
  const [loadingCreateGuest, setLoadingCreateGuest] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/';

  const login = async (username) => {
    setLoadingCreateGuest(true);
    try {
      const res = await fetch(process.env.REACT_APP_APP_URL + '/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username })
      });
      const result = await res.json();
      setData(result);
      window.location.href = redirectPath;
    } catch (err) {
      console.error(err);
      alert("Login failed.");
    } finally {
      setLoadingCreateGuest(false);
    }
  };

  const handleCreateGuest = () => {
    if (!userName.trim()) {
      alert('Please enter your name');
      return;
    }
    login(userName);
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#f8f9fa] dark:bg-black overflow-x-hidden relative font-sans">
       <div className="absolute inset-0">
         <div className="absolute inset-0 z-10 h-full w-full bg-white dark:bg-black bg-[linear-gradient(to_right,#8080801a_1px,transparent_1px),linear-gradient(to_bottom,#8080801a_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:14px_24px] [&>div]:absolute [&>div]:left-0 [&>div]:right-0 [&>div]:top-0 [&>div]:-z-10 [&>div]:m-auto [&>div]:h-[310px] [&>div]:w-[310px] [&>div]:rounded-full [&>div]:bg-fuchsia-400 [&>div]:opacity-20 [&>div]:blur-[100px]">
          <div></div>
        </div>
       </div>
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&family=Permanent+Marker&display=swap');
        
        .bg-paper-texture {
          background-color: #f8f9fa;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E");
        }
        .dark .bg-paper-texture {
          background-color: #000000ff;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.1'/%3E%3C/svg%3E");
        }

        .font-marker { font-family: 'Permanent Marker', cursive; }
        .font-body { font-family: 'Outfit', sans-serif; }
        
        .btn-sketch {
          position: relative;
          border: 3px solid #1e293b; 
          box-shadow: 4px 4px 0 0 #1e293b;
          transition: all 0.1s ease;
        }
        .dark .btn-sketch {
          border: 3px solid #374151;
          box-shadow: 4px 4px 0 0 #374151;
        }
        .btn-sketch:active {
          transform: translate(4px, 4px);
          box-shadow: 0px 0px 0 0 #1e293b;
        }
        .dark .btn-sketch:active {
          box-shadow: 0px 0px 0 0 #374151;
        }
        .btn-sketch-primary { 
          background-color: #a5b4fc; 
          color: #1e293b; 
        }
        .dark .btn-sketch-primary { 
          background-color: #4f46e5; 
          color: white; 
        }
        .btn-sketch-secondary { 
          background-color: #fff; 
          color: #1e293b; 
        }
        .dark .btn-sketch-secondary { 
          background-color: #374151; 
          color: white; 
        }
        .btn-sketch-accent { 
          background-color: #fde047; 
          color: #1e293b; 
        }
        .dark .btn-sketch-accent { 
          background-color: #ca8a04; 
          color: white; 
        }
      `}</style>

      <FloatingShapes />

      <header className="relative z-20 w-full py-6 px-6">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          
          <Link to="/" className="flex items-center gap-2 group sm:px-5 sm:py-2 p-1 transition-transform hover:scale-105">
                        <span className='bg-[#452829] dark:bg-[#F5AFAF] text-[#FCF8F8] dark:text-[#452829] p-1.5 rounded-lg group-hover:rotate-12 transition-transform duration-300'>
                          {Icons.Doodle}
                        </span>
                        <span className="font-body font-bold text-lg tracking-tight hidden sm:block text-[#452829] dark:text-[#FBEFEF]">
                          doodle<span className='text-[#F5AFAF]'>up</span>
                        </span>
                      </Link>
          <div className="flex items-center gap-6 font-bold">
            <div className="flex items-center gap-6">
              <a href="https://github.com/jtshgit/doodleup" target='_blank' rel="noreferrer" className="hidden sm:flex items-center gap-2 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-400 dark:hover:border-gray-500 border border-2 border-slate-300 dark:border-gray-600 pl-2 pr-2 py-1 rounded-full transition-colors font-body font-bold">
                {Icons.Github} Star
              </a>
            </div>
            <a href="https://jitesh.unibrik.com/projects/doodleup-a-realtime-collaborative-whiteboard" target='_blank' rel="noreferrer" className="text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition-colors font-body">
              About
            </a>
            <Profile data={data} />
          </div>
        </div>
      </header>

      <main className="relative h-[90vh] z-10 flex-grow flex items-center justify-center px-4 py-12">
        <div className="max-w-3xl mx-auto w-full text-center flex flex-col items-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
           <div className="inline-block mb-10 rotate-[-5deg]">
              <span className="font-marker px-4 py-2 bg-[#F9DFDF] dark:bg-[#F5AFAF]/20 border-2 border-[#452829] dark:border-[#F9DFDF] shadow-[3px_3px_0_0_#452829] dark:shadow-[3px_3px_0_0_#F9DFDF] text-[#452829] dark:text-[#FCF8F8] rounded-sm">
                Free & No Signup Required!
              </span>
            </div>
            
            <h1 className="font-body font-black text-6xl sm:text-7xl md:text-8xl tracking-tighter text-slate-900 dark:text-white leading-none mb-6">
              Draw Together.<br/>
              <span className="font-marker text-indigo-500 dark:text-indigo-400 relative inline-block mt-2">
                Right Now.
                <motion.svg 
                  className="absolute -bottom-4 left-0 w-full h-6 text-indigo-300 dark:text-indigo-700 -z-10" 
                  viewBox="0 0 200 20" fill="none"
                >
                  <motion.path 
                    d="M2 15C40 5 150 5 198 15" 
                    stroke="currentColor" strokeWidth="6" strokeLinecap="round" 
                    style={{strokeDasharray: "10 10"}}
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                  />
                </motion.svg>
              </span>
            </h1>
            
            <p className="font-body text-xl sm:text-2xl text-slate-600 dark:text-gray-400 max-w-xl mx-auto">
              The simplest collaborative whiteboard for teams, friends, and quick ideas.
            </p>
          </motion.div>

          <div className="w-full max-w-md mx-auto min-h-[180px] flex flex-col items-center justify-start relative">
          {data ? (
  data.req ? (
    <motion.div 
      initial={{ opacity: 0, y: 10 }} 
      animate={{ opacity: 1, y: 0 }}
    >
      <Link 
        to="/b"
        className="group relative flex items-center gap-3 pr-4 pl-4 py-2 bg-[#452829] dark:bg-[#FBEFEF] text-[#FCF8F8] dark:text-[#452829] rounded-xl transition-all duration-300 hover:shadow-[0_20px_40px_-15px_rgba(69,40,41,0.3)] dark:hover:shadow-[0_20px_40px_-15px_rgba(251,239,239,0.2)] hover:-translate-y-1 active:scale-95 font-bold text-md"
      >
        <span>Create New Board</span>
        <div className="bg-[#F5AFAF] p-1 rounded-lg group-hover:rotate-90 transition-transform duration-300">
          <svg className="w-6 h-6 text-[#452829]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </Link>
    </motion.div>
  ) : (
    <div className="w-full max-w-md">
      {!enterName ? (
        <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
          <button 
        onClick={() => setEnterName(true)} 
        className="group relative flex items-center gap-3 pr-3 pl-5 py-3 bg-[#452829] dark:bg-[#FBEFEF] text-[#FCF8F8] dark:text-[#452829] rounded-xl transition-all duration-300 hover:shadow-[0_20px_40px_-15px_rgba(69,40,41,0.3)] dark:hover:shadow-[0_20px_40px_-15px_rgba(251,239,239,0.2)] hover:-translate-y-1 active:scale-95 font-bold text-lg"
      >
        <span>Quick Join</span>
        <div className="bg-[#F5AFAF] p-1 rounded-lg transition-transform duration-300">
          {Icons.Guest}
        </div>
      </button>
         
          
          <a 
            href={`${process.env.REACT_APP_ACCOUNT_URL}/a/login?re=${process.env.REACT_APP_URL}`} 
            className="flex-1 px-8 py-4 bg-[#452829] text-white font-bold rounded-xl hover:opacity-90 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-[#452829]/20"
          >
            Login to Save
          </a>
        </div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, scale: 0.98, y: 10 }} 
          animate={{ opacity: 1, scale: 1, y: 0 }} 
          className="w-full bg-white dark:bg-[#3d2b2b] p-8 rounded-[2rem] border border-[#F9DFDF] dark:border-[#4d3838] shadow-2xl"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-[#452829] dark:text-white font-black text-xl">Join Workspace</h3>
            <button onClick={() => setEnterName(false)} className="text-[#F5AFAF] hover:text-[#452829] transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          
          <input
            type="text"
            autoFocus
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            className="w-full px-6 py-4 bg-[#FCF8F8] dark:bg-[#2d1f1f] border-2 border-[#F9DFDF] dark:border-[#4d3838] rounded-2xl focus:outline-none focus:border-[#F5AFAF] transition-all text-[#452829] dark:text-white font-bold mb-4 placeholder-[#452829]/30"
            placeholder="Type your name..."
          />
          
          <button 
            onClick={handleCreateGuest} 
            className="w-full py-4 bg-[#F5AFAF] text-[#452829] font-black rounded-2xl hover:bg-[#f29999] transition-all transform active:scale-[0.98] shadow-md"
          >
            START DRAWING
          </button>
        </motion.div>
      )}
    </div>
  )
) : null}
          </div>
        </div>
      </main>

   {/* =========================================================================
          FEATURES SECTION
         ========================================================================= */}
      <section className="relative z-10 w-full px-4 sm:px-6 py-24 bg-[#FCF8F8] dark:bg-[#1a1111]">
        {/* Background Blob */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#F5AFAF]/5 dark:bg-[#F5AFAF]/5 rounded-full blur-3xl pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative text-center mb-20 max-w-2xl mx-auto"
        >
          <span className="text-xs font-extrabold uppercase tracking-widest text-[#F5AFAF] mb-4 block">
            Why We Built This
          </span>
          <h2 className="font-body font-bold text-4xl md:text-5xl text-[#452829] dark:text-[#F9DFDF] leading-tight">
            Designed for <span className="relative inline-block">
              creatives
              <span className="absolute bottom-1 left-0 w-full h-3 bg-[#F5AFAF]/30 -z-10 rounded-full transform -rotate-1"></span>
            </span> who love to 
            <span className="font-body ml-2 text-[#452829] dark:text-[#FBEFEF]">
              doodle<span className='text-[#F5AFAF]'>up</span>
            </span>
          </h2>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {DoodleUpInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white dark:bg-[#2d1f1f] rounded-[2rem] p-8 border border-[#452829]/5 dark:border-[#F5AFAF]/10 shadow-xl shadow-[#452829]/5 dark:shadow-black/20 hover:shadow-2xl hover:shadow-[#452829]/10 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-[#FBEFEF] dark:bg-[#1a1111] text-[#452829] dark:text-[#F5AFAF] shadow-inner`}>
                  {item.icon}
                </div>
                <h3 className="font-body font-bold text-xl text-[#452829] dark:text-[#F9DFDF] mb-3">
                  {item.title}
                </h3>
                <p className="font-body font-medium text-[#452829]/60 dark:text-[#FBEFEF]/60 leading-relaxed">
                  {item.description}
                </p>
                
                {/* Decorative corner accent */}
                <div className="absolute top-8 right-8 w-2 h-2 rounded-full bg-[#F5AFAF] opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================================
          FOOTER SECTION
         ========================================================================= */}
      <footer className="relative z-10 w-full pt-24 pb-12 px-6 bg-[#FBEFEF]/30 dark:bg-[#0f0a0a] border-t border-[#452829]/5 dark:border-[#F5AFAF]/5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
          
          {/* Brand Column */}
          <div className="col-span-1 md:col-span-2 space-y-6">
            <Link to="/" className="flex items-center gap-2 group w-fit">
              <span className='bg-[#452829] dark:bg-[#F5AFAF] text-[#FBEFEF] dark:text-[#452829] p-2 rounded-xl group-hover:rotate-12 transition-transform duration-300'>
                {Icons.Doodle}
              </span>
              <span className="font-body font-bold text-2xl tracking-tight text-[#452829] dark:text-[#F9DFDF]">
                doodle<span className='text-[#F5AFAF]'>up</span>
              </span>
            </Link>
            <p className="font-body font-medium text-[#452829]/60 dark:text-[#FBEFEF]/50 max-w-sm text-lg leading-relaxed">
              Making remote collaboration as tactile, fluid, and fun as a physical notebook.
            </p>
          </div>
          
          {/* Links Column 1 */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-widest text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-6">Project</h4>
            <ul className="space-y-4">
              <li><a target='_blank' href="https://jitesh.unibrik.com/projects/doodleup-a-realtime-collaborative-whiteboard" className="font-bold text-[#452829] dark:text-[#FBEFEF] hover:text-[#F5AFAF] dark:hover:text-[#F5AFAF] transition-colors">About</a></li>
              <li><a target='_blank' href="https://github.com/jtshgit/doodleup" className="font-bold text-[#452829] dark:text-[#FBEFEF] hover:text-[#F5AFAF] dark:hover:text-[#F5AFAF] transition-colors">Open Source</a></li>
              <li><a target='_blank' href="https://unibrik.com" className="font-bold text-[#452829] dark:text-[#FBEFEF] hover:text-[#F5AFAF] dark:hover:text-[#F5AFAF] transition-colors">Unibrik</a></li>
            </ul>
          </div>

          {/* Links Column 2 */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-widest text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-6">Connect</h4>
            <ul className="space-y-4">
              <li><a target='_blank' href="https://jtsh.vercel.app" className="font-bold text-[#452829] dark:text-[#FBEFEF] hover:text-[#F5AFAF] dark:hover:text-[#F5AFAF] transition-colors">Developer</a></li>
              <li><a target='_blank' href="https://unibrik.com/support" className="font-bold text-[#452829] dark:text-[#FBEFEF] hover:text-[#F5AFAF] dark:hover:text-[#F5AFAF] transition-colors">Support</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="max-w-6xl mx-auto pt-8 border-t border-[#452829]/10 dark:border-[#FBEFEF]/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="font-bold text-xs text-[#452829]/40 dark:text-[#FBEFEF]/30">
            © {new Date().getFullYear()} DoodleUp. All rights reserved.
          </p>
        </div>
        
      </footer>
    </div>
  );
}